import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navigation from '@/components/Navigation';
import { Card } from '@/components/ui/card';
import { ArrowRight, BookOpen } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function Frameworks({ user }) {
    const [frameworks, setFrameworks] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchFrameworks = async () => {
            try {
                const response = await axios.get(`${BACKEND_URL}/api/frameworks`, { withCredentials: true });
                setFrameworks(response.data);
            } catch (error) {
                console.error('Error fetching frameworks:', error);
                toast.error("Erro ao carregar os frameworks.");
            } finally {
                setLoading(false);
            }
        };
        fetchFrameworks();
    }, []);

    return (
        <div className="min-h-screen bg-background">
            <Navigation user={user} />

            <main className="container mx-auto px-4 md:px-8 py-16">
                <div className="mb-16">
                    <div className="flex items-center gap-3 mb-4">
                        <BookOpen className="h-8 w-8 text-primary" strokeWidth={1.5} />
                        <h1 className="text-4xl md:text-5xl font-bold tracking-tight leading-none" style={{ fontFamily: 'Manrope, sans-serif' }}>
                            Frameworks de Implementação
                        </h1>
                    </div>
                    <p className="text-lg text-muted-foreground max-w-2xl">
                        Ferramentas práticas e guiadas para aplicar conceitos de Inteligência Artificial no seu negócio.
                        Preencha os frameworks para gerar o seu AI First Plan.
                    </p>
                </div>

                {loading ? (
                    <div className="flex justify-center py-20">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                    </div>
                ) : (
                    <div className="space-y-16">
                        {[
                            {
                                title: 'Fase 1: Diagnóstico & Descoberta',
                                description: 'Entenda o cenário atual, encontre a restrição e levante as oportunidades.',
                                items: frameworks.filter(f => f.order >= 1 && f.order <= 3)
                            },
                            {
                                title: 'Fase 2: Design & Planejamento (O Plano)',
                                description: 'Estruturação técnica, lógica, riscos e métricas da solução de IA.',
                                items: frameworks.filter(f => f.order >= 4 && f.order <= 11)
                            },
                            {
                                title: 'Fase 3: Dinâmicas & Business',
                                description: 'Exercícios focados em cultura e viabilidade financeira (P&L).',
                                items: frameworks.filter(f => f.order >= 12)
                            }
                        ].map((group, gIndex) => group.items.length > 0 && (
                            <section key={gIndex} className="animate-in fade-in-50 duration-500 delay-100">
                                <div className="mb-6 border-b border-border pb-4">
                                    <h2 className="text-2xl font-bold font-[Manrope] text-foreground">{group.title}</h2>
                                    <p className="text-muted-foreground mt-1">{group.description}</p>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                    {group.items.map((framework, index) => (
                                        <Link key={framework.framework_id} to={`/frameworks/${framework.framework_id}`}>
                                            <Card className="flex flex-col h-full hover:border-primary/50 transition-all duration-300 rounded-xl overflow-hidden border border-border group bg-card hover:shadow-md">
                                                <div className="p-6 flex-1 flex flex-col">
                                                    <div className="flex items-center justify-between mb-4">
                                                        <div className="flex items-center justify-center w-12 h-12 rounded-lg bg-primary/10 text-primary font-bold">
                                                            {framework.acronym || String(framework.order).padStart(2, '0')}
                                                        </div>
                                                        <span className="text-xs font-medium px-2 py-1 rounded-full bg-secondary text-secondary-foreground">
                                                            Etapa {framework.order}
                                                        </span>
                                                    </div>

                                                    <h3 className="text-xl font-bold mb-2 group-hover:text-primary transition-colors">
                                                        {framework.name}
                                                    </h3>

                                                    <p className="text-sm text-muted-foreground mb-6 flex-1 line-clamp-3">
                                                        {framework.description}
                                                    </p>

                                                    <div className="pt-4 border-t border-border mt-auto flex items-center justify-between text-sm font-medium">
                                                        <span className="text-muted-foreground group-hover:text-foreground transition-colors">
                                                            Acessar Framework
                                                        </span>
                                                        <ArrowRight className="h-4 w-4 transform group-hover:translate-x-1 transition-transform" />
                                                    </div>
                                                </div>
                                            </Card>
                                        </Link>
                                    ))}
                                </div>
                            </section>
                        ))}
                    </div>
                )}
            </main>
        </div>
    );
}
